/**
 * Select com pesquisa — a escolha única do painel inteiro.
 *
 * Está num arquivo próprio, e não no `ui/index.tsx` com as outras peças, porque
 * ele não é pequeno: tem teclado, foco, lista filtrada e posicionamento. As
 * outras peças de lá cabem em dez linhas cada.
 *
 * ## Por que trocar o `<select>` nativo
 *
 * O nativo é ótimo em lista curta e péssimo nas nossas: o seletor de veículo
 * tem a frota inteira, o de usuário da auditoria tem todo mundo que já mexeu no
 * cadastro, e o de cerca tem uma linha por área. Numa lista de 200, o nativo só
 * oferece rolar — e o type-ahead dele casa pelo **começo** do rótulo, então
 * procurar "900" em "Ônibus 900" não acha nada.
 *
 * ## A pesquisa se esconde sozinha
 *
 * Abaixo de `searchFrom` opções (7 por padrão) a caixa de busca não aparece: um
 * campo de texto em cima de três opções é mais trabalho que a lista. O
 * componente é um só; o que varia é o que ele mostra. Quem precisar da busca
 * sempre passa `searchFrom={0}`.
 *
 * ## A busca ignora acento e casa no meio
 *
 * "onibus" acha "Ônibus", "900" acha "Ônibus 900", "ssa" acha "SSA01 · Base
 * Salvador". É pt-BR: exigir o acento certo numa caixa de busca transforma
 * ajuda em obstáculo, e quem digita rápido não acentua.
 *
 * ## A lista é desenhada no `body`, posicionada à mão
 *
 * O corpo da gaveta de cadastro é `overflow-y-auto`, e 37 dos campos vivem lá
 * dentro. Lista posicionada por `absolute` seria **cortada na borda do
 * contêiner** — e o campo de baixo da gaveta abriria uma lista invisível, que
 * parece campo quebrado. Então ela vai para o `body` por portal, com
 * `position: fixed` nas coordenadas do botão, e reposiciona enquanto a gaveta
 * rola.
 *
 * ## Acessibilidade
 *
 * É um `combobox` de verdade: `role`, `aria-expanded`, `aria-activedescendant`,
 * navegação por seta, Enter para escolher, Esc para sair devolvendo o foco. O
 * `aria-label` chega inteiro no botão, que é o que leitor de tela anuncia — e é
 * por ele que o teste de navegador encontra o campo.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { filterOptions, type SelectOption } from "../../lib/select.ts";

/**
 * A opção, como o `lib/select.ts` a define.
 *
 * O alias existe para quem importa de `ui` não precisar saber de onde vem a
 * regra de busca — e a regra mora lá porque o Node roda `.ts` e não `.tsx`:
 * lógica dentro do componente é lógica que só um navegador exercita.
 */
export type SearchSelectOption = SelectOption;

export interface SearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly SearchSelectOption[];
  /** Rótulo da opção vazia. Omitir torna o campo obrigatório na prática. */
  emptyLabel?: string | undefined;
  /** O que o leitor de tela anuncia, e por onde o teste de navegador acha o campo. */
  ariaLabel?: string | undefined;
  /** Acima de quantas opções a caixa de busca aparece. `0` = sempre. */
  searchFrom?: number;
  disabled?: boolean;
  className?: string;
  /** Texto quando o filtro não acha nada. */
  emptyResult?: string;
}

const ITEM_HEIGHT = 32;
const MAX_VISIBLE = 8;

export function SearchSelect({
  value,
  onChange,
  options,
  emptyLabel,
  ariaLabel,
  searchFrom = 7,
  disabled = false,
  className = "",
  emptyResult = "nada encontrado",
}: SearchSelectProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [active, setActive] = useState(0);
  const [box, setBox] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);

  const root = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);

  // A opção vazia é uma opção como as outras: ela precisa ser escolhível de
  // volta depois de alguém ter escolhido algo.
  const all = useMemo<readonly SearchSelectOption[]>(
    () => (emptyLabel === undefined ? options : [{ value: "", label: emptyLabel }, ...options]),
    [options, emptyLabel],
  );
  const visible = useMemo(() => filterOptions(all, term), [all, term]);
  const selected = all.find((o) => o.value === value);
  const showSearch = all.length >= searchFrom && searchFrom > 0;

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const alvo = event.target as Node;
      // O painel está no `body`, fora da raiz: sem checar os dois, qualquer
      // clique DENTRO da lista contaria como clique fora e a fecharia.
      if (root.current?.contains(alvo) || panel.current?.contains(alvo)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open]);

  /**
   * Onde desenhar a lista, em coordenadas de viewport.
   *
   * Abre para baixo; se não couber, abre para cima. Sem isto, o último campo de
   * uma gaveta abriria a lista fora da área visível e pareceria que nada
   * aconteceu ao clicar.
   */
  const place = useCallback(() => {
    const rect = button.current?.getBoundingClientRect();
    if (!rect) return;
    const needed = Math.min(visible.length, MAX_VISIBLE) * ITEM_HEIGHT + (showSearch ? 44 : 0) + 16;
    const dropUp = rect.bottom + needed > window.innerHeight && rect.top > needed;
    setBox({
      left: rect.left,
      width: rect.width,
      ...(dropUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, [visible.length, showSearch]);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, place]);

  // Reposiciona enquanto a gaveta rola, em vez de fechar: quem abriu a lista e
  // rolou sem querer não perde a escolha. `capture` porque o scroll acontece no
  // contêiner da gaveta, não na janela.
  useEffect(() => {
    if (!open) return;
    let frame = 0;
    const onMove = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    // O foco vai para a busca quando ela existe; quando não existe, fica no
    // botão, que é quem escuta as setas.
    if (showSearch) input.current?.focus();
    const index = visible.findIndex((o) => o.value === value);
    setActive(index >= 0 ? index : 0);
  }, [open, showSearch, value, visible]);

  // Mantém a opção destacada visível ao navegar por seta.
  useEffect(() => {
    if (!open || !list.current) return;
    const node = list.current.children[active] as HTMLElement | undefined;
    node?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  function choose(option: SearchSelectOption): void {
    onChange(option.value);
    setOpen(false);
    setTerm("");
    button.current?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent): void {
    if (!open) {
      if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((i) => Math.min(visible.length - 1, i + 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActive((i) => Math.max(0, i - 1));
        break;
      case "Home":
        event.preventDefault();
        setActive(0);
        break;
      case "End":
        event.preventDefault();
        setActive(visible.length - 1);
        break;
      case "Enter": {
        event.preventDefault();
        const option = visible[active];
        if (option) choose(option);
        break;
      }
      case "Escape":
        event.preventDefault();
        setOpen(false);
        setTerm("");
        button.current?.focus();
        break;
      case "Tab":
        // Tab sai do campo: fechar é o que o nativo faz, e deixar aberto
        // deixaria uma lista flutuando sobre o campo seguinte.
        setOpen(false);
        setTerm("");
        break;
      default:
        break;
    }
  }

  return (
    <div ref={root} className={`relative ${className}`}>
      <button
        ref={button}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-haspopup="listbox"
        {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKeyDown}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border border-line bg-panel px-2.5 py-1.5 text-left text-xs transition-colors focus:border-accent focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${
          selected && selected.value !== "" ? "text-ink" : "text-faint"
        }`}
      >
        <span className="min-w-0 truncate">{selected?.label ?? emptyLabel ?? "—"}</span>
        <span aria-hidden className="shrink-0 text-[10px] text-muted">
          ▾
        </span>
      </button>

      {open && box
        ? createPortal(
            <div
              ref={panel}
              style={{
                position: "fixed",
                left: box.left,
                width: Math.max(box.width, 192),
                ...(box.top !== undefined ? { top: box.top } : {}),
                ...(box.bottom !== undefined ? { bottom: box.bottom } : {}),
              }}
              // Acima da gaveta (z-30) e do diálogo de confirmação (z-40), e
              // abaixo do modal "Ver tudo" (z-2000), que nunca tem select.
              className="z-[100] overflow-hidden rounded-lg border border-line bg-panel shadow-xl"
            >
          {showSearch ? (
            <div className="border-b border-line p-1.5">
              <input
                ref={input}
                value={term}
                onChange={(event) => {
                  setTerm(event.target.value);
                  setActive(0);
                }}
                onKeyDown={onKeyDown}
                placeholder="buscar"
                aria-label={ariaLabel ? `Buscar em ${ariaLabel}` : "Buscar"}
                className="w-full rounded-md border border-line bg-app px-2 py-1 text-xs text-ink placeholder:text-faint focus:border-accent focus:outline-none"
              />
            </div>
          ) : null}

          {visible.length === 0 ? (
            <p className="px-2.5 py-3 text-center text-[11px] text-faint">{emptyResult}</p>
          ) : (
            <ul
              ref={list}
              id={`${id}-list`}
              role="listbox"
              aria-activedescendant={visible[active] ? `${id}-opt-${active}` : undefined}
              className="max-h-64 overflow-y-auto py-1"
            >
              {visible.map((option, index) => (
                <li
                  key={option.value || "__vazio__"}
                  id={`${id}-opt-${index}`}
                  role="option"
                  aria-selected={option.value === value}
                  onMouseEnter={() => setActive(index)}
                  // `preventDefault` no mousedown para o clique não tirar o
                  // foco da caixa de busca antes de o `click` acontecer; a
                  // escolha fica no `click`, que é o evento que um teste de
                  // navegador dispara e que o teclado também produz.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(option)}
                  className={`cursor-pointer px-2.5 py-1.5 text-xs ${
                    index === active ? "bg-subtle" : ""
                  } ${option.value === value ? "font-medium text-accent" : "text-ink"}`}
                >
                  <span className="block truncate">{option.label}</span>
                  {option.hint ? <span className="block truncate text-[10px] text-muted">{option.hint}</span> : null}
                </li>
              ))}
            </ul>
          )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/** Rótulo + campo, para o formulário não repetir a mesma marcação. */
export function SearchSelectField({
  label,
  hint,
  required = false,
  ...props
}: SearchSelectProps & { label: string; hint?: ReactNode; required?: boolean }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </span>
      <div className="mt-1.5">
        <SearchSelect {...props} ariaLabel={props.ariaLabel ?? label} />
      </div>
      {hint ? <span className="mt-1 block text-[11px] leading-snug text-muted">{hint}</span> : null}
    </label>
  );
}
